import _ from 'lodash';
import { answerQuestion, answerQuestionNew, answerShortEssayQuestion,
	answerShortEssayQuestionNew, skipShortEssayQuestion,
	startQuiz, getPretestQuiz, fetchStudentQuizReviewData } from 'common/api';
import Toast from 'common/Toast';
import log from 'common/Logger';
import { loadState } from '../../localstorage';
import { getStudent, isTeacher } from 'common/UserService';
import { BASE_PATH, constants } from 'config';
import Utils from 'common/Utils';
import { getUser } from '../../../../common/UserService';

const persistedState = loadState() || {};

const state = persistedState.quiz || {
	title: '',
	image: '',
	level: 'One',
	lexileLevel: 0,
	imageLink: '',
	footnotes: '',
	command: {},
	associatedTextPassages: {},
	questions: [],
	passage: '',
	step: 0,
	lastQuizTime: 0,
	textSize: 'medium',
	isAnswerFirstScrolled: false,
	isFastAnswered: false,
	isLoading: false,
	responseText: '',
	quizComplete: false,
	isReviewMode: false,
	reviewCommand: {},
	student: {},
	isLevelUp: false,
	bonusScoreAwardRangeName: '',
	isReadyForRealQuiz: false,
	isPretestQuiz: false,
	pretestComplete: false,
	targetPretestNumber: 0,
	pretestQuizzesTaken: 0,
	resultStep: 0,
	user: null,
	error: null,
	progressLevels: [],
	isQuestionSkipped: false,
	isActivityModalVisible: false
}

const getters = {}

const makeQuizData = (quizCommandData) => {
	const quizCommand = quizCommandData.command || {};
	const questions = quizCommandData.questions || [];
	const step = !quizCommandData.isPretestQuiz ? questions.findIndex(question => question.correct === undefined) : (quizCommandData.isForUpdating ? quizCommandData.pretestQuizzesTaken - 1 : quizCommandData.pretestQuizzesTaken);

	const quizData = {
		command: quizCommand,
		associatedTextPassages: quizCommandData.associatedTextPassages || {},
		title: quizCommand.title,
		level: quizCommand.level,
		lexileLevel: quizCommand.lexileLevel,
		imageLink: quizCommand.imageLinkDest,
		passage: quizCommand.passage,
		footnotes: quizCommand.footnotes,
		quizComplete: quizCommand.complete,
		questions,
		step,
		responseText: '',
		isLoading: false,
		isReviewMode: false,
		lastQuizTime: new Date().getTime(),
		isPretestQuiz: quizCommandData.isPretestQuiz,
		pretestComplete: quizCommandData.pretestComplete,
		targetPretestNumber: quizCommandData.targetPretestNumber,
		pretestQuizzesTaken: quizCommandData.pretestQuizzesTaken,
		user: getUser(),
		progressLevels: quizCommand.progressLevels,
		isQuestionSkipped: false
	};
	return quizData;
}

const mutations = {
	setQuizStep(state, step) {
		state.step = step;
	},
	increaseStep(state) {
		state.step = state.step + 1;
		state.error = null;
	},
	decreaseStep(state) {
    	state.step = state.step - 1;
	},
	initResult(state) {
		state.resultStep = 0;
		state.isQuestionSkipped = false;
	},
	increaseResultStep(state) {
		state.resultStep = state.resultStep + 1;
	},
	setActivityModalVisible(state, isVisible) {
		state.isActivityModalVisible = isVisible;
	},
	setQuizData(state, quizData) {
		state.reviewCommand = {};
		state.isLevelUp = false;
		_.keys(quizData).map(key => {
			state[key] = quizData[key];
		});
	},
	setQuizError(state, error) {
		state.error = error;
	},
	setQuizDataFromReviewCommand(state, reviewCommand) {
		_.keys(reviewCommand).map(key => {
			switch(key) {
				case 'imageLinkDest':
					state.imageLink = reviewCommand.imageLinkDest;
					break;
				case 'questionList':
					state.questions = reviewCommand.questionList;
					break;
				case 'complete':
					state.quizComplete = reviewCommand.complete;
					break;
				default:
					state[key] = reviewCommand[key];
					break;
			}
		});
		if (state.shortEssayQuestionList) {
			const questions = state.questions;
			state.shortEssayQuestionList.forEach(question => questions.push(question));
		}
		state.reviewCommand = reviewCommand;
		state.reviewCommand.isHistory = true;
		state.isReviewMode = true;
		state.step = 0;
		state.isLoading = false;
	},
	updateCurrentQuestion(state, questionData) {
		const currQuestion = state.questions[state.step];
		_.keys(questionData).map(key => {
			currQuestion[key] = questionData[key];
		});
		state.questions = [
			...state.questions.slice(0, state.step),
			currQuestion,
			...state.questions.slice(state.step + 1),
		];
	},
	setQuestionSkipped(state) {
		state.isQuestionSkipped = true;
	},
	setQuizResultData(state, quizResult) {
		const sessionStudent = getStudent();
		state.reviewCommand = quizResult.reviewCommand;
		state.student = quizResult.user.student;
		state.student.avatar = BASE_PATH + '/assets' + quizResult.userAvatar;
		state.bonusScoreAwardRangeName = quizResult.bonusScoreAwardRangeName;
		const oldStudentLevel = Utils.getCurrentLevelForStudent(sessionStudent);
		const updatedStudentLevel = Utils.getCurrentLevelForStudent(quizResult.user.student);
		state.isLevelUp = oldStudentLevel && updatedStudentLevel && updatedStudentLevel.progressLevelId > oldStudentLevel.progressLevelId;
	},
	updateLastQuizTime(state) {
		state.lastQuizTime = new Date().getTime();
	},
    setQuizTextSize(state, textSize) {
    	state.textSize = textSize;
	},
	setAnswerFirstScrolled(state) {
		state.isAnswerFirstScrolled = true;
	},
	setFastAnswered(state, fastAnswered) {
		state.isFastAnswered = fastAnswered;
	},
	setLoading(state, isLoading) {
		state.isLoading = isLoading;
	},
	setResponseText(state, text) {
		const question = state.questions[state.step];
		question.responseText = text;
	},
	setQuizCompleted(state, isComplete) {
		state.quizComplete = isComplete;
	},
	setIsReviewMode(state, isReviewMode) {
		state.isReviewMode = isReviewMode;
		if (state.isReviewMode) state.isPretestQuiz = false;
	},
	setPretestComplete(state, isPretestComplete) {
		state.pretestComplete = isPretestComplete;
	},
	setReadyForRealQuiz(state) {
		state.isReadyForRealQuiz = true;
	},
	finishPretest(state) {
		state.isPretestQuiz = false;
		state.isReadyForRealQuiz = false;
	},
}

const actions = {
	initQuizData(context, initialData) {
		const quizData = makeQuizData(initialData);
		context.commit('setQuizData', quizData);
	},
	updatePretestQuizData(context, quizData) {
		const newPretestQuizzesTaken = quizData.pretestQuizzesTaken;
		let newQuestions = [];

		for(let i = 0; i <= newPretestQuizzesTaken; i++) {
			newQuestions.push(i === newPretestQuizzesTaken ? quizData.questions[0] : {});
		}
		const pretestQuizData = {
			...quizData,
			questions: newQuestions
		};
		context.dispatch('initQuizData', pretestQuizData);
	},
	async submitAnswer(context, answerData) {
		const { answerId, timeSpentOnAnswer } = answerData
		context.commit('setLoading', true);
		context.commit('setQuizError', null);
		const currStep = context.state.step;
		const currQuestion = context.state.questions[currStep];
		let result = null;
		if (context.state.isPretestQuiz) {
			result = await answerQuestion(answerId, currQuestion.questionId);
		} else {
			result = await answerQuestionNew(answerId, currQuestion.questionId, timeSpentOnAnswer);
		}

		if (result.success) {
			const questionData = {
				correct: result.message.correct,
				selectedAnswerId: result.message.selectedAnswerId,
				correctAnswerId: result.message.correctAnswerId,
				explanation: result.message.explanation,
				pointsAwarded: result.message.pointsAwarded,
			};
			context.commit('updateCurrentQuestion', questionData);

			setTimeout(() => {
				if (window.innerWidth < 768) {
					document.body.scrollIntoView({behavior: 'smooth', block: 'start'});
				}
			}, 200);

			const questionCount = !context.state.isPretestQuiz ? context.state.questions.length : context.state.targetPretestNumber;
			if (!context.state.isPretestQuiz && currStep === questionCount - 1) {
				if (result.message.studentQuizResponseId) {
					context.commit('setQuizResultData', result.message);
				}
			}
		} else {
			log.error('Quiz - ' + result.message);
			if (result.message.includes(constants.REFRESH_TIMEOUT_ERROR_MSG) || result.message.includes(constants.NOT_FOR_NEXT_ANSWER_ERROR_MSG)) {
				setTimeout(() => {
					location.reload();
				}, 1000)
			} else {
				Toast.error(result.message);
			}
			context.commit('setQuizError', result.message);
		}

		context.commit('setLoading', false);
	},
	async goToNextQuestion(context) {
		const state = context.state;
		const questionCount = !state.isPretestQuiz ? state.questions.length : state.targetPretestNumber;
		if (state.step < questionCount - 1) {
			if (state.isPretestQuiz) {
				context.commit('setLoading', true);
				const pretest = await getPretestQuiz();
				if (pretest.model.command.mergedQuestionList.length > 0) {
					const quizData = {
						pretestComplete: pretest.pretestComplete,
						command: pretest.model.command,
						questions: pretest.model.command.mergedQuestionList,
						associatedTextPassages: pretest.model.associatedTextPassages,
						targetPretestNumber: pretest.model.targetPretestNumber,
						pretestQuizzesTaken: pretest.model.pretestQuizzesTaken,
						isPretestQuiz: true,
						isForUpdating: true
					}
					context.dispatch('updatePretestQuizData', quizData);
				}
				context.commit('setLoading', false);
			}
			context.commit('increaseStep');
			context.commit('updateLastQuizTime');
		} else {
			location.reload();
		}
	},
	async sendResponse(context, timeSpentOnAnswer) {
		context.commit('setLoading', true);
		const currQuestion = context.state.questions[context.state.step];

		let result = null;
        result = await answerShortEssayQuestionNew(currQuestion.shortEssayQuestionId, currQuestion.responseText, false, timeSpentOnAnswer);

		if (result.success) {
			const responseText = currQuestion.responseText;
			const questionData = {
				...result.message,
				responseText
			}
			context.commit('updateCurrentQuestion', questionData);
			if (questionData.studentQuizResponseId) {
				context.commit('setQuizResultData', questionData);
			}
		} else {
			Toast.error(result.message);
		}

		context.commit('setLoading', false);
	},
	async skipShortEssayQuestion(context, timeSpentOnAnswer) {
		context.commit('setLoading', true);
		context.commit('setQuizError', null);

		const currStep = context.state.step;
		const currQuestion = context.state.questions[currStep];
		let result = await answerShortEssayQuestionNew(currQuestion.shortEssayQuestionId, currQuestion.responseText, true, timeSpentOnAnswer);

		if (result.success) {
			context.commit('setQuestionSkipped');
			const responseText = currQuestion.responseText;
			const questionData = {
				...result.message,
				responseText
			}
			context.commit('updateCurrentQuestion', questionData);
			if (questionData.studentQuizResponseId) {
				context.commit('setQuizResultData', questionData);
			}
		}

		context.commit('setLoading', false);
	},
	goToReviewMode(context) {
		context.commit('setIsReviewMode', true);
		context.commit('setQuizStep', 0);
	},
	async goToNextQuiz(context) {
		context.commit('setLoading', true);

		const result = await startQuiz();
		if (result.success) {
			const quizData = makeQuizData(result);
			context.commit('setQuizData', quizData);
		} else {
			Toast.error(result.message);
		}

		context.commit('setLoading', false);
	},
	exitReview(context) {
		if (context.state.reviewCommand.isHistory)  {
			if (isTeacher()) {
				const reviewCommand = context.state.reviewCommand;
				if (!reviewCommand.studentReportUrl) {
					const pageUrl = Utils.isNewReportUIVisible() ? '/app/teacher/reports' : '/teaching/studentReport';
					location.href = BASE_PATH + pageUrl;
				} else {
					location.href = reviewCommand.studentReportUrl;
				}
			} else {
				location.href = BASE_PATH + '/dashboard/index'; // go to progress report
			}
		} else {
			context.commit('setIsReviewMode', false);
			context.commit('setQuizStep', 0);
		}
	}
};

export default {
	namespaced: true,
	state,
	getters,
	mutations,
	actions
};
